import type videojs from 'video.js';
import {
  afterNextRender,
  AfterRenderRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CmsLanguageService, ErrorHandlerService } from '@libs/shared/common';
import { DEFAULT_VIDEO_ASPECT_RATIO } from '../video-content.constants';

export interface VideoJsOptions {
  autoplay: false;
  controls: boolean;
  fill: boolean;
  language?: string;
  languages?: {
    de: Record<string, string>;
  };
  loop: boolean;
  muted: boolean;
  poster: string | undefined;
  preload: 'metadata';
}

@Component({
  selector: 'ui-video-content-player',
  templateUrl: './video-content-player.component.html',
  styleUrls: ['./video-content-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[style.aspect-ratio]': 'aspectRatio()',
    '[style.max-width.px]': 'maxWidth()',
  },
})
export class VideoContentPlayerComponent {
  private readonly targetRef: Signal<ElementRef<HTMLVideoElement>> = viewChild.required('target');
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);
  private readonly errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);

  private player?: ReturnType<typeof videojs>;
  private viewportObserver?: IntersectionObserver;

  private readonly unregisterDestroyCleanup: () => void = this.destroyRef.onDestroy((): void => {
    this.viewportObserver?.disconnect();
    this.player?.dispose();
  });

  private readonly initPlayerRef: AfterRenderRef = afterNextRender((): void => {
    void this.initPlayer();
  });

  public src: InputSignal<string> = input.required();
  public poster: InputSignal<string | undefined> = input();
  public controls: InputSignal<boolean> = input(true);
  public autoplay: InputSignal<boolean> = input(false);
  public loop: InputSignal<boolean> = input(false);
  public aspectRatio: InputSignal<string> = input(DEFAULT_VIDEO_ASPECT_RATIO);
  public maxWidth: InputSignal<number | undefined> = input();
  public readonly playbackError: OutputEmitterRef<void> = output<void>();

  private async initPlayer(): Promise<void> {
    try {
      const { default: videojs } = await import('video.js');

      if (this.destroyRef.destroyed) {
        return;
      }

      const src: string = this.src();

      if (!src) {
        this.playbackError.emit();

        return;
      }

      const options: VideoJsOptions = await this.getVideoOptions();

      if (this.destroyRef.destroyed) {
        return;
      }

      const videoElement: HTMLVideoElement = this.targetRef().nativeElement;

      videoElement.preload = options.preload;
      videoElement.src = src;

      this.player = videojs(videoElement, options);

      this.player.on('error', this.handlePlaybackError);

      if (this.autoplay()) {
        this.player.ready((): void => {
          if (!this.destroyRef.destroyed) {
            this.observeViewportPlayback();
          }
        });
      }
    } catch (error) {
      this.errorHandlerService.handleError(new Error('Video player initialization failed', { cause: error }));
    }
  }

  private async getVideoOptions(): Promise<VideoJsOptions> {
    const options: VideoJsOptions = {
      autoplay: false,
      controls: this.controls(),
      fill: true,
      loop: this.loop(),
      muted: this.autoplay(),
      poster: this.poster(),
      preload: 'metadata',
    };

    if (this.cmsLanguageService.language() !== 'de') {
      return options;
    }

    const { default: germanLanguage } = await import('video.js/dist/lang/de.json');

    options.language = 'de';
    options.languages = {
      de: germanLanguage,
    };

    return options;
  }

  private observeViewportPlayback(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const playerElement: Element | undefined = this.player?.el();

    if (!playerElement) {
      return;
    }

    this.viewportObserver?.disconnect();

    this.viewportObserver = new IntersectionObserver(this.handleViewportPlayback, {
      threshold: 0.5,
    });

    this.viewportObserver.observe(playerElement);
  }

  private readonly handleViewportPlayback = (entries: IntersectionObserverEntry[]): void => {
    const entry: IntersectionObserverEntry | undefined = entries[0];

    if (!entry || !this.player || (this.player.ended() && !this.loop())) {
      return;
    }

    if (entry.isIntersecting) {
      this.player.muted(true); // keep viewport-triggered autoplay eligible across browsers
      this.player.play()?.catch((): undefined => undefined);

      return;
    }

    this.player.pause();
  };

  private readonly handlePlaybackError = (): void => {
    this.viewportObserver?.disconnect();
    this.playbackError.emit();
  };}
